/**
 * Pure vector math for embedding similarity. Vectors are HFM embeddings
 * (one row per cell). Similarity uses cosine, so rows are L2-normalized once
 * and similarity becomes a dot product.
 */

export interface RankedRow {
  row: number
  score: number
}

/** L2-normalizes a vector. A zero vector stays zero. */
export const l2Normalize = (vec: number[]): Float32Array => {
  let norm = 0
  for (let i = 0; i < vec.length; i += 1) norm += vec[i] * vec[i]
  norm = Math.sqrt(norm)
  const out = new Float32Array(vec.length)
  if (norm === 0) return out
  for (let i = 0; i < vec.length; i += 1) out[i] = vec[i] / norm
  return out
}

/** Cosine similarity between two raw vectors (normalizes internally). */
export const cosineSimilarity = (a: number[], b: number[]): number => {
  const na = l2Normalize(a)
  const nb = l2Normalize(b)
  let sum = 0
  for (let i = 0; i < na.length; i += 1) sum += na[i] * nb[i]
  return sum
}

/**
 * Normalized centroid of the given rows of a normalized matrix. The mean of
 * unit vectors gives the dominant direction; re-normalizing keeps it on the
 * unit sphere so it can be compared by dot product.
 */
export const centroidOfRows = (
  matrix: Float32Array,
  dim: number,
  rows: number[]
): Float32Array => {
  const sum = new Float32Array(dim)
  rows.forEach((row) => {
    const base = row * dim
    for (let i = 0; i < dim; i += 1) sum[i] += matrix[base + i]
  })
  let norm = 0
  for (let i = 0; i < dim; i += 1) norm += sum[i] * sum[i]
  norm = Math.sqrt(norm)
  const out = new Float32Array(dim)
  if (norm === 0) return out
  for (let i = 0; i < dim; i += 1) out[i] = sum[i] / norm
  return out
}

/**
 * Ranks candidate rows of a normalized matrix by cosine similarity (dot
 * product) to a normalized query vector, highest first. topK <= 0 returns all.
 */
export const rankRowsBySimilarity = (
  matrix: Float32Array,
  dim: number,
  queryNormalized: Float32Array,
  candidateRows: number[],
  topK: number
): RankedRow[] => {
  const scored = candidateRows.map((row) => {
    const base = row * dim
    let score = 0
    for (let i = 0; i < dim; i += 1) score += matrix[base + i] * queryNormalized[i]
    return { row, score }
  })
  scored.sort((a, b) => b.score - a.score)
  return topK > 0 ? scored.slice(0, topK) : scored
}
